import React from "react";
import styles from "./styles.module.scss";
import coachnow_logo from "app/images/Logo_dark.svg";
import orange_bg_check from "app/images/orange_bg_checkmark.svg";
import { useTranslation } from "react-i18next";
import { AnalyzePlanType } from "./PricingToggle";
import { formatPrice } from "app/utils/currency";
import { PromoDiscount } from "app/models/PromoOffer";
import DiscountedPrice from "app/components/DiscountedPrice";
import TrialPriceLine from "app/components/TrialPriceLine";
import { usePlanPricing } from "app/hooks/usePlanPricing";

// Marketing anchor for the yearly strike-through — not a real Stripe price.
// $120 → $59.99 makes the "-50%" badge accurate.
const YEARLY_ANCHOR_PRICE = "$120";

interface IProps {
  planCode: AnalyzePlanType;
  loading?: boolean;
  // The plan's live base price (cents) for the currency, from the backend.
  baseAmount?: number;
  // The resolved offer's discount (from the backend), or null/undefined.
  discount?: PromoDiscount | null;
  // Direct purchase: no free-trial headline, the price itself leads.
  purchase?: boolean;
  // The paywall's copy (from its GrowthBook flag, defaulting to today's).
  description: string;
  trialHeadline: string;
}

const FEATURES = [
  "5 Spaces & Groups",
  "Video/Image Analysis Suite",
  "250 Media Items in Library",
];

const PriceCard = ({
  planCode,
  loading,
  baseAmount,
  discount,
  purchase = false,
  description,
  trialHeadline,
}: IProps) => {
  const { t } = useTranslation();
  const isYearly = planCode === "analyze+yearly";
  const pricing = usePlanPricing({ baseAmount, discount, isYearly });
  const { base, discountedAmount, isDiscounted, interval } = pricing;

  return (
    <div className={styles.pricing_card}>
      <img
        src={orange_bg_check}
        alt="checked plan"
        className={styles.plan_checkmark}
      />
      <div className={styles.header}>
        <div className={styles.top_side_content}>
          <div className={styles.header_wrapper}>
            <div className={styles.header_logo_wrapper}>
              <img
                src={coachnow_logo}
                alt="CoachNow Logo"
                className={styles.site_logo}
              />
              <span className={styles.plan_label}>{t("Analyze")}</span>
            </div>
          </div>
          <p>{description}</p>
        </div>

        <ul className={styles.features_list}>
          {FEATURES.map((feature) => (
            <li key={feature}>
              <img src={orange_bg_check} alt="" aria-hidden="true" />
              <span>{t(feature)}</span>
            </li>
          ))}
        </ul>

        <div>
          <h3 className={styles.content_title}>
            {loading
              ? t("Applying your offer…")
              : !purchase
              ? trialHeadline
              : isDiscounted
              ? (
                <DiscountedPrice
                  discounted={discountedAmount}
                  original={base}
                  currency="usd"
                  interval={interval}
                />
              )
              : isYearly
              ? t("{{price}} per year", { price: formatPrice(base) })
              : t("{{price}} per month", { price: formatPrice(base) })}
          </h3>
          <p className={styles.content_description}>
            {loading ? null : purchase ? (
              isYearly ? (
                t("Billed annually")
              ) : (
                t("Billed monthly")
              )
            ) : isDiscounted ? (
              <TrialPriceLine pricing={pricing} currency="usd" />
            ) : isYearly ? (
              <>
                {t("Then")}{" "}
                <span className={styles.price_original}>{YEARLY_ANCHOR_PRICE}</span>{" "}
                <span className={styles.price_promo}>
                  {formatPrice(base)}
                </span>{" "}
                {t("per year / billed annually")}
              </>
            ) : (
              t("Then {{price}} per month / billed monthly", {
                price: formatPrice(base),
              })
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceCard;
